import { Download, FileJson, ShieldAlert, Trash2 } from "lucide-react";
import { useState } from "react";

import { gdprService } from "@/api/gdpr";
import { extractErrorMessage } from "@/utils";
import { useStore } from "@/store";

import { Button } from "../Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../Dialog";
import { Textarea } from "../Textarea";
import { useToast } from "../Toast";

type TBusy = "access" | "portability" | "delete" | null;
type TExportFormat = "json" | "csv";

/** Trigger a browser download for the given text content. */
function triggerDownload(content: string, mime: string, filename: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Flatten a nested object/array into dot-path key -> scalar pairs. */
function flatten(value: unknown, prefix = "", out: Record<string, unknown> = {}): Record<string, unknown> {
  if (value === null || value === undefined) {
    if (prefix) out[prefix] = "";
    return out;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) out[prefix] = "";
    else value.forEach((v, i) => flatten(v, prefix ? `${prefix}[${i}]` : `[${i}]`, out));
    return out;
  }
  if (typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 0) out[prefix] = "";
    else keys.forEach((k) => flatten((value as Record<string, unknown>)[k], prefix ? `${prefix}.${k}` : k, out));
    return out;
  }
  out[prefix] = value;
  return out;
}

/** Render an export payload as a flat key,value CSV (lossless for any shape). */
function toCsv(data: unknown): string {
  const escape = (s: unknown) => {
    const str = String(s);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const flat = flatten(data);
  const rows = ["key,value", ...Object.entries(flat).map(([k, v]) => `${escape(k)},${escape(v)}`)];
  return rows.join("\n");
}

/** Download an export payload in the chosen format. */
function downloadExport(data: unknown, baseName: string, format: TExportFormat): void {
  if (format === "csv") {
    triggerDownload(toCsv(data), "text/csv;charset=utf-8", `${baseName}.csv`);
  } else {
    triggerDownload(JSON.stringify(data, null, 2), "application/json", `${baseName}.json`);
  }
}

export const DataPrivacySettings = () => {
  const { toast } = useToast();
  const resetAuthStore = useStore.use.resetAuthStore();

  const [busy, setBusy] = useState<TBusy>(null);
  const [format, setFormat] = useState<TExportFormat>("json");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");

  const handleExport = async () => {
    setBusy("access");
    const { data, error } = await gdprService.requestDataAccess();
    setBusy(null);
    if (error) {
      toast({ variant: "destructive", title: "Export failed", description: await extractErrorMessage(error) });
      return;
    }
    downloadExport(data?.data?.data ?? data?.data ?? {}, "my-data-export", format);
    toast({ variant: "success", title: `Your data export is ready (${format.toUpperCase()})` });
  };

  const handlePortability = async () => {
    setBusy("portability");
    const { data, error } = await gdprService.requestDataPortability();
    setBusy(null);
    if (error) {
      toast({ variant: "destructive", title: "Export failed", description: await extractErrorMessage(error) });
      return;
    }
    downloadExport(data?.data?.export_data ?? data?.data ?? {}, "my-data-portability", format);
    toast({ variant: "success", title: `Portable export downloaded (${format.toUpperCase()})` });
  };

  const handleDelete = async () => {
    setBusy("delete");
    const { error } = await gdprService.requestDeletion({
      reason: deleteReason.trim() || null,
    });
    setBusy(null);
    if (error) {
      toast({ variant: "destructive", title: "Deletion failed", description: await extractErrorMessage(error) });
      return;
    }
    setDeleteOpen(false);
    toast({ variant: "success", title: "Your account has been deleted" });
    // The account no longer exists — clear auth and hard-redirect to login
    // (full reload discards all in-memory app state).
    resetAuthStore();
    window.location.assign("/auth/login");
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold">Data &amp; Privacy</h3>
        <p className="text-sm text-muted-foreground">
          Exercise your data rights. Requests are logged and processed under GDPR.
        </p>
      </div>

      {/* Export */}
      <section className="space-y-3 rounded-lg border bg-background p-4">
        <div>
          <div className="text-sm font-semibold">Export your data</div>
          <div className="text-sm text-muted-foreground">
            Download a copy of the personal data we hold about you (Article 15 / 20).
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor="export-format"
            className="text-sm font-medium text-muted-foreground"
          >
            Format
          </label>
          <select
            id="export-format"
            value={format}
            onChange={(e) => setFormat(e.target.value as TExportFormat)}
            disabled={busy !== null}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="json">JSON</option>
            <option value="csv">CSV</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleExport()}
            disabled={busy !== null}
          >
            <Download className="mr-2 h-4 w-4" />
            {busy === "access" ? "Preparing…" : "Export my data"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void handlePortability()}
            disabled={busy !== null}
          >
            <FileJson className="mr-2 h-4 w-4" />
            {busy === "portability" ? "Preparing…" : "Portable export"}
          </Button>
        </div>
      </section>

      {/* Danger zone */}
      <section className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-destructive" />
          <div className="text-sm font-semibold text-destructive">Delete my account</div>
        </div>
        <div className="text-sm text-muted-foreground">
          Permanently delete your account and associated data (Article 17). This cannot be undone.
        </div>
        <Button
          type="button"
          variant="destructive"
          onClick={() => setDeleteOpen(true)}
          disabled={busy !== null}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete my account
        </Button>
      </section>

      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open && busy !== "delete") setDeleteOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              This permanently deletes your account and personal data and cannot be undone. You will be signed out.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason (optional)"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={busy === "delete"}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={busy === "delete"}
            >
              {busy === "delete" ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
